import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../Spinner';

export default function PermissionManagerModal({ show, onHide, user, onSave }) {
  const { token } = useAuth();
  const [permissions, setPermissions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [extraPermissions, setExtraPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [allPermsRes, rolesRes, userRoleRes] = await Promise.all([
        fetch(`${API_URL}/permissions`),
        fetch(`${API_URL}/roles`),
        fetch(`${API_URL}/users/${user.id}/role`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (!allPermsRes.ok || !rolesRes.ok || !userRoleRes.ok) {
        throw new Error('Errore nel caricamento dei dati');
      }

      const allPerms = await allPermsRes.json();
      const rolesData = await rolesRes.json();
      const userRoleData = await userRoleRes.json();

      setPermissions(allPerms);
      setRoles(rolesData.roles);
      setUserRole(userRoleData.role);
      setRolePermissions(userRoleData.role_permissions || []);
      setExtraPermissions(userRoleData.extra_permissions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, user]);

  useEffect(() => {
    if (show && user) {
      fetchData();
    }
  }, [show, user, fetchData]);

  const handleRoleChange = (newRole) => {
    setUserRole(newRole);
    // Update role permissions based on selected role
    const selectedRole = roles.find(r => r.name === newRole);
    setRolePermissions(selectedRole?.permissions || []);
  };

  const handleToggleExtra = (permName) => {
    // Don't allow toggling role-enforced permissions
    if (rolePermissions.includes(permName)) {
      return;
    }

    setExtraPermissions(prev =>
      prev.includes(permName)
        ? prev.filter(p => p !== permName)
        : [...prev, permName]
    );
  };

  const isPermissionEnabled = (permName) => {
    return rolePermissions.includes(permName) || extraPermissions.includes(permName);
  };

  const isPermissionLocked = (permName) => {
    return rolePermissions.includes(permName);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // First update the role
      const roleResponse = await fetch(`${API_URL}/users/${user.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: userRole })
      });

      if (!roleResponse.ok) {
        const data = await roleResponse.json();
        throw new Error(data.detail || 'Errore nell\'aggiornamento del ruolo');
      }

      // Then update extra permissions (role permissions will be enforced by backend)
      const allPermissions = [...new Set([...rolePermissions, ...extraPermissions])];
      const permResponse = await fetch(`${API_URL}/users/${user.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ permissions: allPermissions })
      });

      if (!permResponse.ok) {
        const data = await permResponse.json();
        throw new Error(data.detail || 'Errore nell\'aggiornamento dei permessi');
      }

      if (onSave) {
        onSave(user.id, allPermissions);
      }
      onHide();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Group permissions by category
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const cat = perm.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(perm);
    return acc;
  }, {});

  // Category display names
  const categoryNames = {
    hike: 'Gite',
    user: 'Utenti',
    system: 'Sistema'
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            Gestisci permessi - {user?.nome} {user?.cognome}
          </h2>
          <p className="text-sm text-gray-500 mt-1">{user?.email}</p>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Role Selector */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Ruolo
                </label>
                <select
                  value={userRole || ''}
                  onChange={(e) => handleRoleChange(e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  {roles.map(role => (
                    <option key={role.name || 'none'} value={role.name || ''}>
                      {role.label}
                    </option>
                  ))}
                </select>
                {userRole && (
                  <p className="text-sm text-gray-500 mt-2">
                    I permessi evidenziati in blu sono richiesti dal ruolo e non possono essere rimossi.
                  </p>
                )}
              </div>

              {/* Permissions by Category */}
              {Object.entries(groupedPermissions).map(([category, perms]) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    {categoryNames[category] || category}
                  </h3>
                  <div className="space-y-2">
                    {perms.map(perm => {
                      const enabled = isPermissionEnabled(perm.name);
                      const locked = isPermissionLocked(perm.name);

                      return (
                        <label
                          key={perm.id}
                          className={`flex items-start p-3 rounded-lg border transition-colors ${
                            locked
                              ? 'border-blue-500 bg-blue-50 cursor-not-allowed'
                              : enabled
                              ? 'border-green-500 bg-green-50 cursor-pointer'
                              : 'border-gray-200 hover:bg-gray-50 cursor-pointer'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={() => handleToggleExtra(perm.name)}
                            disabled={locked}
                            className={`mt-1 h-4 w-4 border-gray-300 rounded focus:ring-blue-500 ${
                              locked ? 'text-blue-600 cursor-not-allowed' : 'text-green-600'
                            }`}
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-center gap-2">
                              <code className="text-sm font-mono text-gray-900">
                                {perm.name}
                              </code>
                              {locked && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                  dal ruolo
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              {perm.description}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onHide}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}
